type StringifyParameters = Parameters<typeof JSON.stringify>;

type JSONStringify<T = any> = (
  value: T,
  replacer?: StringifyParameters['1'],
  space?: StringifyParameters['2']
) => string;

type JSONParse<T = any> = (...args: Parameters<typeof JSON.parse>) => T | null;

export const KIZEN_PREFIX = 'kizen-';

export class LocalStorage<T = any> {
  private key: string;
  private localStorage: typeof window.localStorage | null;
  private deserialize: JSONParse<T>;
  private serialize: JSONStringify<T>;

  constructor(
    key: string,
    { serialize = JSON.stringify, deserialize = JSON.parse } = {}
  ) {
    this.key = key;
    this.serialize = serialize;
    this.deserialize = deserialize;

    try {
      this.localStorage = window.localStorage;
    } catch {
      this.localStorage = null;
    }
  }

  get val(): T | null {
    if (this.localStorage) {
      try {
        const val = this.localStorage.getItem(this.key);
        if (val) {
          return this.deserialize(val);
        }
      } catch {}
    }
    return null;
  }

  set val(val: T) {
    if (this.localStorage) {
      try {
        this.localStorage.setItem(this.key, this.serialize(val));
      } catch {}
    }
  }

  clear() {
    if (this.localStorage) {
      try {
        this.localStorage.removeItem(this.key);
      } catch {}
    }
  }
}

export class SessionStorage<T = any> {
  key: string;
  sessionStorage: typeof window.sessionStorage | null;
  deserialize: JSONParse<T>;
  serialize: JSONStringify<T>;

  constructor(
    key: string,
    { serialize = JSON.stringify, deserialize = JSON.parse } = {}
  ) {
    this.key = key;
    this.serialize = serialize;
    this.deserialize = deserialize;
    try {
      this.sessionStorage = window.sessionStorage;
    } catch {
      this.sessionStorage = null;
    }
  }

  get val(): T | null {
    if (this.sessionStorage) {
      try {
        const val = this.sessionStorage.getItem(this.key);
        if (val) {
          return this.deserialize(val);
        }
      } catch {}
    }
    return null;
  }

  set val(val: T) {
    if (this.sessionStorage) {
      try {
        this.sessionStorage.setItem(this.key, this.serialize(val));
      } catch {}
    }
  }

  clear() {
    if (this.sessionStorage) {
      try {
        this.sessionStorage.removeItem(this.key);
      } catch {}
    }
  }
}
